
import React, { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { ProfileData } from '@/components/profile/ProfileContext';

// Refactored component imports
import DashboardLayout from '@/components/dashboard/DashboardLayout';
import DashboardContent from '@/components/dashboard/DashboardContent';
import ZerodhaConnectionHandler from '@/components/dashboard/ZerodhaConnectionHandler';

const Dashboard = () => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [profileData, setProfileData] = useState<ProfileData | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Load profile data
  useEffect(() => {
    const loadProfileData = async () => {
      if (!user) return;

      try {
        const { data, error } = await supabase
          .from('investor_profiles')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false })
          .limit(1);

        if (error) {
          console.error('Database error loading profile:', error);
          return;
        }

        const profileRow = data && data.length > 0 ? data[0] : null;

        if (profileRow) {
          // Map the data to our ProfileData structure
          setProfileData({
            basicInfo: profileRow.basic_info || {},
            financialGoals: profileRow.financial_goals || {},
            riskAssessment: profileRow.risk_assessment || {},
            currentPortfolio: profileRow.current_portfolio || {},
            taxInfo: profileRow.tax_info || {},
            investmentPreferences: profileRow.investment_preferences || {}
          });
        }
      } catch (error) {
        console.error('Error loading profile data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadProfileData();
  }, [user]);

  const handleSignOut = async () => {
    await signOut();
    navigate('/auth');
  };

  // Check if profile has at least some data
  const hasProfileData = () => {
    if (!profileData) return false;
    
    // Check if any section has data
    return Object.values(profileData).some(section => 
      section && Object.keys(section).length > 0
    );
  };

  // Check if profile is complete (all sections have data)
  const isProfileComplete = () => {
    if (!profileData) return false;
    
    // Check if all sections have data
    return Object.values(profileData).every(section => 
      section && Object.keys(section).length > 0
    );
  };

  if (!user) {
    return (
      <DashboardLayout>
        <div className="text-white">Loading user data...</div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout 
      isLoading={isLoading}
      profileData={profileData}
      hasProfileData={hasProfileData()}
      isProfileComplete={isProfileComplete()}
    >
      {/* Include the ZerodhaConnectionHandler properly */}
      <ZerodhaConnectionHandler />
      
      {!isLoading && (
        <>
          {/* Welcome Section */}
          <div className="mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-white">
                  Welcome back, {user?.user_metadata?.full_name?.split(' ')[0] || 'Investor'}!
                </h1>
                <p className="text-gray-300 mt-1">
                  {isProfileComplete 
                    ? "Your portfolio is ready for AI-powered insights"
                    : "Complete your setup to unlock personalized recommendations"
                  }
                </p>
              </div>
              
              {/* Quick Stats */}
              <div className="flex gap-4">
                <div className="text-center p-3 bg-[#9E7C47]/10 rounded-lg border border-[#9E7C47]/20">
                  <div className="text-sm text-gray-300">Profile</div>
                  <div className="text-lg font-bold text-[#F9F1A9]">
                    {Math.round((Object.values(profileData || {}).filter(section => 
                      section && Object.keys(section).length > 0
                    ).length / 6) * 100)}%
                  </div>
                </div>
                <div className="text-center p-3 bg-[#9E7C47]/10 rounded-lg border border-[#9E7C47]/20">
                  <div className="text-sm text-gray-300">Zerodha</div>
                  <div className="text-lg font-bold text-[#F9F1A9]">
                    {localStorage.getItem('kite_access_token') ? '✓' : '○'}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Main Dashboard Content */}
          <DashboardContent onSignOut={handleSignOut} />
        </>
      )}
    </DashboardLayout>
  );
};

export default Dashboard;
